/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import LogoDark from '../../images/logo/gitme-logo-white.svg';
import TurnstileWidget from './TurnstileWidget';
import Loading from '../../components/Loading';

/* ================= API ENDPOINTS ================= */
const API_GET_INVITE_INFO_URL = '/invite/get-invite-info';
const API_ACCEPT_INVITE_URL = '/invite/accept-invite';

const AcceptInvite = () => {
  const { tokenId } = useParams();
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const turnstileRef = useRef(null);

  // Verify token and get invite info
  useEffect(() => {
    const verifyToken = async () => {
      if (!tokenId) {
        setError('Invalid invitation link');
        setVerifying(false);
        return;
      }

      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}${API_GET_INVITE_INFO_URL}/${tokenId}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await res.json();

        if (res.ok && data.success) {
          setInviteInfo(data.data);
          setError(null);
        } else {
          setError(data.message || 'Invalid or expired invitation');
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setError('Failed to verify invitation. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [tokenId]);

  const handleAccept = async () => {
    // Validation
    if (!firstName.trim()) {
      toast.error('First name is required');
      return;
    }

    if (!lastName.trim()) {
      toast.error('Last name is required');
      return;
    }

    if (!password || typeof password !== 'string') {
      toast.error('Password is required');
      return;
    }

    if (!turnstileToken) {
      toast.error('Please complete the verification');
      return;
    }

    const isWeakPassword = !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(
      password
    );

    if (isWeakPassword) {
      toast.error(
        'Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number.'
      );
      return;
    }

    if (!password || !confirmPassword) {
      toast.error('Please fill out all fields.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}${API_ACCEPT_INVITE_URL}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: tokenId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          password,
          verifyToken: turnstileToken
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success('Account created successfully! You can now sign in.');

        // If login token is provided, save it and redirect
        if (data.data?.token) {
          localStorage.setItem('authToken', data.data.token);
          navigate('/');
        } else {
          // Otherwise redirect to sign in
          setTimeout(() => {
            navigate('/signIn');
          }, 2000);
        }
      } else {
        setError(data.message || 'Failed to accept invitation');
        toast.error(data.message || 'Failed to accept invitation');
        // Reset Turnstile on error
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
        setTurnstileToken('');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
      // Reset Turnstile on error
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return <Loading type="page" />;
  }

  if (error && !inviteInfo) {
    return (
      <div className="min-h-screen flex bg-gray-100">
        <div className="w-full flex items-center justify-center p-10 bg-white">
          <div className="w-full max-w-md text-center">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Invalid Invitation
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/signIn')}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Left - Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-10 bg-white">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold mb-2 text-gray-900">
            Accept Invitation
          </h2>
          <p className="text-gray-600 mb-6">
            You've been invited to join{' '}
            <span className="font-semibold">
              {inviteInfo?.organizationName || 'this organization'}
            </span>
            . Please complete your account setup.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={inviteInfo?.email || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <input
              type="text"
              value={inviteInfo?.role}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <VisibilityOffIcon className="h-5 w-5" />
                ) : (
                  <VisibilityIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={
                  showConfirmPassword ? 'Hide password' : 'Show password'
                }
              >
                {showConfirmPassword ? (
                  <VisibilityOffIcon className="h-5 w-5" />
                ) : (
                  <VisibilityIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Password must be at least <strong>8 characters</strong> long and
            include at least one <strong>uppercase letter</strong>, one{' '}
            <strong>lowercase letter</strong>, and one <strong>number</strong>.
          </p>

          <div className="mb-6 flex justify-center">
            <TurnstileWidget onSuccess={setTurnstileToken} ref={turnstileRef} />
          </div>

          <button
            onClick={handleAccept}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading
              ? 'Creating Account...'
              : 'Accept Invitation & Create Account'}
          </button>
        </div>
      </div>

      {/* Right - Branding */}
      <div className="hidden md:flex w-1/2 bg-blue-600 items-center justify-center p-10 rounded-l-[2rem] flex-col text-center text-white">
        <img
          src={LogoDark}
          alt="Logo"
          className="mb-6"
          style={{ width: '10rem' }}
        />
        <p className="text-lg font-semibold mb-2 max-w-md">
          A New Standard in Developer Performance Analytics.
        </p>
        <p className="text-sm font-light max-w-md leading-relaxed">
          Gitme leverages advanced AI to deliver accurate, unified, and
          transparent insights — replacing fragmented metrics with a single
          source of truth.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
